/**
 * 敏感字段展示
 *
 * 文档 4.2 字段权限 / 8.1 敏感数据：税号、银行账号等字段在无字段权限时脱敏。
 * 脱敏规则与后端共用 @ilc/shared 的实现（maskTaxNo / maskBankAccount），
 * 避免前端脱敏后仍与后端明文长度不一致而泄露信息。
 *
 * 用法：
 *   <MaskedField value={invoice.sellerTaxNo} kind="taxNo" permission={PermissionCode.INVOICE_READ} />
 */

import { useState } from 'react';
import { Button, Space, Tag, Tooltip, Typography } from 'antd';
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons';
import { maskAddress, maskBankAccount, maskIdCard, maskPhone, maskTaxNo } from '@ilc/shared';
import { useAuthStore } from '@/stores/auth';

export type MaskKind = 'taxNo' | 'bankAccount' | 'phone' | 'idCard' | 'address' | 'none';

export interface MaskedFieldProps {
  value: string | null | undefined;
  /** 脱敏规则 */
  kind?: MaskKind;
  /** 需要该权限码才显示明文；不传则该字段一律脱敏（如无对应权限码的自定义字段） */
  permission?: string;
  emptyText?: string;
  /** 允许有权限的用户临时查看明文 */
  allowReveal?: boolean;
  /** 等宽字体（税号、账号等数字串便于核对） */
  mono?: boolean;
}

function applyMask(value: string, kind: MaskKind): string {
  switch (kind) {
    case 'taxNo':
      return maskTaxNo(value);
    case 'bankAccount':
      return maskBankAccount(value);
    case 'phone':
      return maskPhone(value);
    case 'idCard':
      return maskIdCard(value);
    case 'address':
      return maskAddress(value);
    case 'none':
    default:
      return value;
  }
}

export function MaskedField({
  value,
  kind = 'taxNo',
  permission,
  emptyText = '-',
  allowReveal = false,
  mono = true,
}: MaskedFieldProps): React.ReactElement {
  const can = useAuthStore((s) => s.can);
  const [revealed, setRevealed] = useState(false);

  if (value === null || value === undefined || value === '') {
    return <span style={{ color: 'rgba(0,0,0,0.35)' }}>{emptyText}</span>;
  }

  const hasFieldPermission = permission ? can(permission) : false;
  const masked = applyMask(String(value), kind);
  const display = hasFieldPermission && revealed ? String(value) : hasFieldPermission ? masked : masked;

  const text = (
    <span style={{ fontFamily: mono ? 'var(--ilc-mono)' : undefined, letterSpacing: mono ? '0.02em' : undefined }}>
      {display}
    </span>
  );

  if (!hasFieldPermission) {
    return (
      <Tooltip title="当前角色无该字段的明文查看权限，已按脱敏规则展示（文档 4.2 字段权限）">
        <Space size={4}>
          {text}
          <Tag color="default" style={{ marginInlineEnd: 0, fontSize: 11, lineHeight: '16px', padding: '0 4px' }}>
            已脱敏
          </Tag>
        </Space>
      </Tooltip>
    );
  }

  return (
    <Space size={4}>
      {text}
      {allowReveal ? (
        <Tooltip title={revealed ? '隐藏明文' : '查看明文（操作会记录日志）'}>
          <Button
            type="text"
            size="small"
            icon={revealed ? <EyeInvisibleOutlined /> : <EyeOutlined />}
            onClick={() => setRevealed((v) => !v)}
          />
        </Tooltip>
      ) : null}
    </Space>
  );
}

/** 纯文本脱敏（不适合用组件时，如导出、tooltip 文案） */
export function maskText(value: string | null | undefined, kind: MaskKind = 'taxNo'): string {
  if (!value) return '';
  return applyMask(String(value), kind);
}

/** 页面级提示：当前账号存在脱敏字段时，说明原因 */
export function MaskHint(): React.ReactElement | null {
  const auth = useAuthStore((s) => s.auth);
  if (!auth) return null;
  const roleNames = auth.roles.map((r) => r.name).join('、') || '当前角色';
  return (
    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
      税号、银行账号等敏感字段按「{roleNames}」的字段权限展示，无权限时以脱敏形式呈现（不影响业务操作，如需明文请联系租户管理员分配权限）。
    </Typography.Text>
  );
}

export default MaskedField;
